const IDENTITY_KEYS = new Set([
  "id",
  "locale",
  "questionId",
  "ticketingPrizeId",
  "variantId",
  "valueId",
  "sortIndex",
  "amount",
  "isRecurrent",
  "recurrenceInterval",
]);

type UnknownRecord = Record<string, unknown>;

const isPlainObject = (value: unknown): value is UnknownRecord =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const takeCounterpart = (own: unknown, counterpart: unknown): unknown =>
  counterpart === undefined && typeof own === "string" ? "" : counterpart;

function swapValues(left: unknown, right: unknown): [unknown, unknown] {
  if (Array.isArray(left) && Array.isArray(right)) {
    return swapArrays(left, right);
  }

  if (isPlainObject(left) && isPlainObject(right)) {
    return swapObjects(left, right);
  }

  return [takeCounterpart(left, right), takeCounterpart(right, left)];
}

function swapArrays(left: unknown[], right: unknown[]): [unknown[], unknown[]] {
  const swappedLeft = [...left];
  const swappedRight = [...right];

  for (let index = 0; index < Math.min(left.length, right.length); index++) {
    [swappedLeft[index], swappedRight[index]] = swapValues(left[index], right[index]);
  }

  return [swappedLeft, swappedRight];
}

function swapObjects(left: UnknownRecord, right: UnknownRecord): [UnknownRecord, UnknownRecord] {
  const swappedLeft: UnknownRecord = { ...left };
  const swappedRight: UnknownRecord = { ...right };

  for (const key of new Set([...Object.keys(left), ...Object.keys(right)])) {
    if (IDENTITY_KEYS.has(key)) {
      continue;
    }
    [swappedLeft[key], swappedRight[key]] = swapValues(left[key], right[key]);
  }

  return [swappedLeft, swappedRight];
}

export const swapTranslationContent = <T extends object>(fields: T, translations: T): [T, T] => {
  const [swappedFields, swappedTranslations] = swapObjects(fields as UnknownRecord, translations as UnknownRecord);

  return [swappedFields as T, swappedTranslations as T];
};
